import React from 'react';
import { Divider, Avatar, Space, Button, Popconfirm, Tag, Typography, theme, Row, Col, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { hasActivePercentageDiscount } from '../../../Utils/date';
import {
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CrownOutlined,
    SafetyOutlined,
    DollarOutlined
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * Renders a single labeled field with consistent spacing.
 * @param {string} label - Field label text
 * @param {React.ReactNode} value - Field value content
 * @param {Object} token - Ant Design theme token
 */
const InfoField = ({ label, value, token }) => (
    <div style={{ marginBottom: 16 }}>
        <Text
            type="secondary"
            style={{
                display: 'block',
                fontSize: 12,
                marginBottom: 4,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
            }}
        >
            {label}
        </Text>
        <div style={{ fontSize: 14 }}>
            {value || <Text type="secondary">—</Text>}
        </div>
    </div>
);

const UserInfo = ({ user, onDeleteProfilePicture }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    if (!user) {
        return null;
    }

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num || 0);
    };

    const formatDate = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
        if (!date) return null;
        return moment(date).format(format);
    };

    const hasActiveDiscount = hasActivePercentageDiscount(
        user.discount_percentage,
        user.discount_expiry
    );

    const sectionStyle = {
        padding: 16,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgLayout,
        marginBottom: 16,
    };

    return (
        <>
            {/* Profile Header */}
            <Space
                direction="horizontal"
                size={16}
                align="start"
                style={{ marginBottom: 24, width: '100%' }}
            >
                <Avatar
                    src={user.picture_url}
                    size={80}
                    style={{ border: `2px solid ${token.colorBorder}`, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 18, display: 'block' }}>
                        {user.full_name}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                        {user.email}
                    </Text>
                    {user.picture_url && onDeleteProfilePicture && (
                        <Popconfirm
                            title={t('delete_profile_picture')}
                            okText={t('yes')}
                            cancelText={t('no')}
                            onConfirm={() => onDeleteProfilePicture(user.id)}
                        >
                            <Tooltip title={t('delete_profile_picture')}>
                                <Button
                                    danger
                                    size="small"
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    style={{ marginTop: 8 }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </div>
            </Space>

            {/* Basic Information */}
            <div style={sectionStyle}>
                <Row gutter={[24, 0]}>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('gender')}
                            value={user.gender}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('locale')}
                            value={user.locale}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('timezone')}
                            value={user.timezone}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('account_active')}
                            value={
                                !user.is_disabled
                                    ? <Tag icon={<CheckCircleOutlined />} color="success">{t('yes')}</Tag>
                                    : <Tag icon={<CloseCircleOutlined />} color="default">{t('no')}</Tag>
                            }
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('admin_access')}
                            value={
                                user.is_admin
                                    ? <Tag icon={<CrownOutlined />} color="gold">{t('yes')}</Tag>
                                    : <Tag color="default">{t('no')}</Tag>
                            }
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('verified')}
                            value={
                                user.is_verified
                                    ? <Tag icon={<SafetyOutlined />} color="blue">{t('yes')}</Tag>
                                    : <Tag color="default">{t('no')}</Tag>
                            }
                            token={token}
                        />
                    </Col>
                </Row>
            </div>

            {/* Account Details */}
            <Divider orientation="left" style={{ marginTop: 8, marginBottom: 16 }}>{t('account_details')}</Divider>
            <div style={sectionStyle}>
                <Row gutter={[24, 0]}>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('created_at')}
                            value={formatDate(user.created_at)}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('updated_at')}
                            value={formatDate(user.updated_at)}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('welcome_credit_at')}
                            value={formatDate(user.welcome_credit_at)}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('discount_added_at')}
                            value={formatDate(user.discount_added_at)}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('discount_percentage')}
                            value={
                                user.discount_percentage
                                    ? (
                                        <Tag color={hasActiveDiscount ? 'orange' : 'default'}>
                                            {hasActiveDiscount
                                                ? `${user.discount_percentage}%`
                                                : `${user.discount_percentage}% (${t('expired')})`}
                                        </Tag>
                                    )
                                    : <Text type="secondary">0%</Text>
                            }
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('discount_expiry')}
                            value={formatDate(user.discount_expiry, 'YYYY-MM-DD')}
                            token={token}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <InfoField
                            label={t('discount_added_by')}
                            value={user.discount_added_by?.full_name}
                            token={token}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <InfoField
                            label={t('note')}
                            value={user.note}
                            token={token}
                        />
                    </Col>
                </Row>
            </div>

            {/* Balance Details */}
            <Divider orientation="left" style={{ marginTop: 8, marginBottom: 16 }}>{t('balance_details')}</Divider>
            <div style={sectionStyle}>
                <Row gutter={[24, 0]}>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('available_coins')}
                            value={
                                <Text strong style={{ color: token.colorSuccess }}>
                                    <DollarOutlined /> {formatNumber(user.balance?.available_coins)}
                                </Text>
                            }
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('total_coins_bought')}
                            value={formatNumber(user.balance?.total_coins_bought)}
                            token={token}
                        />
                    </Col>
                    <Col xs={12} sm={8}>
                        <InfoField
                            label={t('total_coins_consumed')}
                            value={formatNumber(user.balance?.total_coins_consumed)}
                            token={token}
                        />
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default UserInfo;
